import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronUp, History, FileText, MessageSquare, CheckCircle2 } from 'lucide-react'
import { useState } from 'react'
import { bobAnalyseService } from '@/lib/services/bobAnalyseService'
import { formatDateTime } from '@/lib/utils/timeFormat'
import { useHasPermission } from '@/lib/hooks/usePermission'
import { useCrisisStore } from '@/lib/stores/crisisStore'

interface SourceHistoryPanelProps {
  bobAnalyseId: string
}

export function SourceHistoryPanel({ bobAnalyseId }: SourceHistoryPanelProps) {
  const [isOpen, setIsOpen] = useState(false)
  const { activeCrisis } = useCrisisStore()
  const canReadBob = useHasPermission('bob.read')

  // Fetch source history
  const { data: sourceHistory, isLoading } = useQuery({
    queryKey: ['source-history', bobAnalyseId],
    queryFn: () => bobAnalyseService.getSourceHistory(bobAnalyseId),
    enabled: !!bobAnalyseId && !!activeCrisis && canReadBob,
    refetchInterval: 3000, // Refresh every 3 seconds to show new sources added
  })

  if (!canReadBob) return null

  const isEmpty = !sourceHistory || sourceHistory.length === 0
  const itemsToShow = isOpen ? 6 : 2
  const displayedItems = sourceHistory?.slice(0, itemsToShow) || []
  const hasMore = (sourceHistory?.length || 0) > itemsToShow

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <History className="w-3 h-3 text-gray-600" />
          <span className="font-medium text-sm text-gray-900">Bronnen ({sourceHistory?.length || 0})</span>
        </div>
        {!isEmpty && (
          isOpen ? (
            <ChevronUp className="w-3 h-3 text-gray-600" />
          ) : (
            <ChevronDown className="w-3 h-3 text-gray-600" />
          )
        )}
      </button>

      {/* Always show items (first 3 by default, first 6 when expanded) */}
      {!isEmpty && (
        <div className="px-3 py-2 space-y-2">
          {isLoading ? (
            <div className="text-xs text-gray-600 py-2">Laden...</div>
          ) : (
            <>
              <div className="space-y-2">
                {displayedItems.map((source) => (
                  <div
                    key={source.id}
                    className="flex items-start gap-2 p-2 bg-gray-50 rounded border border-gray-200 hover:border-blue-300 transition-colors"
                  >
                    {/* Source type icon */}
                    <div className="flex-shrink-0 pt-0.5">
                      {source.type === 'scan' ? (
                        <FileText className="w-3 h-3 text-blue-600" />
                      ) : (
                        <MessageSquare className="w-3 h-3 text-purple-600" />
                      )}
                    </div>

                    {/* Source details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col gap-1">
                        <span className="font-medium text-xs text-gray-900 truncate">
                          {source.type === 'scan' ? source.scan?.title || 'Scan' : source.label || 'Input'}
                        </span>

                        {source.processedAt && (
                          <div className="flex items-center gap-1 text-xs text-green-700">
                            <CheckCircle2 className="w-2.5 h-2.5 flex-shrink-0" />
                            <span className="truncate">{formatDateTime(new Date(source.processedAt))}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Show more indicator */}
              {hasMore && (
                <button
                  onClick={() => setIsOpen(true)}
                  className="w-full text-xs text-blue-600 hover:text-blue-700 py-1 px-2 rounded hover:bg-blue-50 transition-colors"
                >
                  +{(sourceHistory?.length || 0) - itemsToShow} meer
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Empty state */}
      {isEmpty && !isLoading && (
        <div className="px-3 py-2">
          <div className="text-xs text-gray-600 py-2">Geen bronnen.</div>
        </div>
      )}
    </div>
  )
}
